import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import Logo from '../../Pages/images/HKAU.png';
import './BookingModal.css';
import { API_BASE_URL } from '../../utils/constants';

const timeSlots = [
  '10:00 AM',
  '11:30 AM',
  '02:00 PM',
  '03:30 PM',
  '05:00 PM'
];

const monthsList = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const daysOfWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const countries = [
  { code: '+91', name: 'India', flag: 'https://flagcdn.com/in.svg', id: 'IN' },
  { code: '+971', name: 'UAE', flag: 'https://flagcdn.com/ae.svg', id: 'AE' },
  { code: '+1', name: 'USA', flag: 'https://flagcdn.com/us.svg', id: 'US' },
  { code: '+44', name: 'UK', flag: 'https://flagcdn.com/gb.svg', id: 'GB' },
  { code: '+61', name: 'Australia', flag: 'https://flagcdn.com/au.svg', id: 'AU' },
  { code: '+65', name: 'Singapore', flag: 'https://flagcdn.com/sg.svg', id: 'SG' },
];

const BookingModal = ({ show, handleClose }) => {
  // Booking Steps: 1 = Date & Time Selection, 2 = Enter Details, 3 = Booking Success
  const [step, setStep] = useState(1);

  // Date & Time Selection State
  const [currentDate, setCurrentDate] = useState(new Date()); // Default starts at current month/year
  const [selectedDate, setSelectedDate] = useState(new Date()); // Selected today
  const [selectedTime, setSelectedTime] = useState(''); // Initialized dynamically in useEffect
  const [timezone, setTimezone] = useState('India'); // Selected timezone (India or Dubai)

  // Details Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [meetingType, setMeetingType] = useState('Online');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [meetLink, setMeetLink] = useState('');

  // Reset modal state when opened
  useEffect(() => {
    if (show) {
      const today = new Date();
      setCurrentDate(today);
      setSelectedDate(today);
      setTimezone('India');

      const isPastSlot = (timeStr, dateObj) => {
        const slotTimeObj = getSlotDateTime(timeStr, dateObj, 'India');
        if (!slotTimeObj) return false;
        return slotTimeObj < new Date();
      };

      const firstAvailable = timeSlots.find(slot => !isPastSlot(slot, today));
      setSelectedTime(firstAvailable || '');

      setErrors({});
      setName('');
      setEmail('');
      setPhone('');
      setSelectedCountry(countries[0]);
      setLocation('');
      setMeetingType('Online');
      setNotes('');
      setMeetLink('');
      setStep(1);
    }
  }, [show]);

  // Calendar Helper Functions
  const handlePrevMonth = () => {
    setCurrentDate(prev => {
      const year = prev.getFullYear();
      const month = prev.getMonth();
      return month === 0 ? new Date(year - 1, 11, 1) : new Date(year, month - 1, 1);
    });
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const year = prev.getFullYear();
      const month = prev.getMonth();
      return month === 11 ? new Date(year + 1, 0, 1) : new Date(year, month + 1, 1);
    });
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1).getDay();
    // Convert Sunday=0, Monday=1 to Monday=0, Sunday=6
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const totalDays = new Date(year, month + 1, 0).getDate();
    const prevTotalDays = new Date(year, month, 0).getDate();

    const days = [];

    // Prev month padding days
    for (let i = startOffset - 1; i >= 0; i--) {
      days.push({
        day: prevTotalDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevTotalDays - i)
      });
    }

    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }

    // Next month padding days
    const totalSlots = 42; // 6 rows * 7 columns
    const nextPadding = totalSlots - days.length;
    for (let i = 1; i <= nextPadding; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }

    return days;
  };

  const isSameDay = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  const isPastDay = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const getSlotDateTime = (timeStr, dateObj, tz) => {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    let [_, hoursStr, minutesStr, ampm] = match;
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    if (ampm.toUpperCase() === 'PM' && hours < 12) {
      hours += 12;
    } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }
    const offset = tz === 'Dubai' ? '+04:00' : '+05:30';
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const hh = String(hours).padStart(2, '0');
    const min = String(minutes).padStart(2, '0');
    return new Date(`${yyyy}-${mm}-${dd}T${hh}:${min}:00${offset}`);
  };

  const isPastTimeSlot = (timeStr, dateObj) => {
    const slotTimeObj = getSlotDateTime(timeStr, dateObj, timezone);
    if (!slotTimeObj) return false;
    return slotTimeObj < new Date();
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // Find the first available time slot for this date
    const firstAvailable = timeSlots.find(slot => {
      const slotTimeObj = getSlotDateTime(slot, date, timezone);
      if (!slotTimeObj) return false;
      return slotTimeObj < new Date();
    });
    setSelectedTime(firstAvailable || '');
  };

  const handleTimezoneChange = (newTz) => {
    setTimezone(newTz);
    if (selectedDate) {
      const firstAvailable = timeSlots.find(slot => {
        const slotTimeObj = getSlotDateTime(slot, selectedDate, newTz);
        if (!slotTimeObj) return false;
        return slotTimeObj < new Date();
      });
      setSelectedTime(firstAvailable || '');
    }
  };

  const getFormattedSelectedDate = () => {
    const day = selectedDate.getDate();
    const year = selectedDate.getFullYear();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return `${weekdays[selectedDate.getDay()]}, ${day} ${months[selectedDate.getMonth()]} ${year}`;
  };

  // Verify selected slot before proceeding
  const handleNextStep = () => {
    if (!selectedDate) {
      toast.error('Please select a date from the calendar.');
      return;
    }
    if (!selectedTime) {
      toast.error('Please select a time slot.');
      return;
    }
    setStep(2);
  };

  const getCleanedPhoneNumber = (rawPhone, countryObj) => {
    let val = rawPhone.trim().replace(/[\s-()]/g, '');
    if (val.startsWith('+')) {
      val = val.substring(1);
    }
    const cleanCC = countryObj?.code?.replace('+', '') || '';
    if (cleanCC && val.startsWith(cleanCC) && val.length > cleanCC.length) {
      val = val.substring(cleanCC.length);
    }
    if (val.startsWith('0') && val.length > 1) {
      val = val.substring(1);
    }
    return val;
  };

  // Form Submission Validation
  const validateForm = () => {
    const tempErrors = {};

    // Name validation
    const nameVal = name.trim();
    if (!nameVal) {
      tempErrors.name = 'Full Name is required';
    } else if (nameVal.length < 3) {
      tempErrors.name = 'Full Name must be at least 3 characters long';
    } else if (!/^[A-Za-z\s]+$/.test(nameVal)) {
      tempErrors.name = 'Full Name can only contain letters and spaces';
    }

    // Email validation
    const emailVal = email.trim();
    if (!emailVal) {
      tempErrors.email = 'Email Address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      tempErrors.email = 'Please enter a valid email address';
    }

    // Phone validation based on selected country
    const phoneVal = getCleanedPhoneNumber(phone, selectedCountry);
    if (!phoneVal) {
      tempErrors.phone = 'Phone number is required';
    } else {
      switch (selectedCountry.id) {
        case 'IN': // India
          if (!/^\d{10}$/.test(phoneVal)) {
            tempErrors.phone = 'India phone number must be exactly 10 digits';
          }
          break;
        case 'US': // USA
          if (!/^\d{10}$/.test(phoneVal)) {
            tempErrors.phone = 'US phone number must be exactly 10 digits';
          }
          break;
        case 'AE': // UAE
          if (!/^\d{7,9}$/.test(phoneVal)) {
            tempErrors.phone = 'UAE phone number must be between 7 and 9 digits';
          }
          break;
        case 'GB': // UK
          if (!/^\d{9,10}$/.test(phoneVal)) {
            tempErrors.phone = 'UK phone number must be 9 or 10 digits';
          }
          break;
        case 'AU': // Australia
          if (!/^\d{9}$/.test(phoneVal)) {
            tempErrors.phone = 'Australia phone number must be exactly 9 digits';
          }
          break;
        case 'SG': // Singapore
          if (!/^\d{8}$/.test(phoneVal)) {
            tempErrors.phone = 'Singapore phone number must be exactly 8 digits';
          }
          break;
        default:
          if (!/^\d{7,15}$/.test(phoneVal)) {
            tempErrors.phone = 'Please enter a valid phone number (7 to 15 digits)';
          }
          break;
      }
    }

    // Location validation
    const locationVal = location.trim();
    if (!locationVal) {
      tempErrors.location = 'Location/City is required';
    } else if (locationVal.length < 2) {
      tempErrors.location = 'Location/City must be at least 2 characters long';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Handle Details Submission
  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const cleanedPhone = getCleanedPhoneNumber(phone, selectedCountry);
    const localYear = selectedDate.getFullYear();
    const localMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const localDay = String(selectedDate.getDate()).padStart(2, '0');
    const localDateStr = `${localYear}-${localMonth}-${localDay}`;

    const bookingDetails = {
      name: name.trim(),
      email: email.trim(),
      phone: `${selectedCountry.code} ${cleanedPhone}`,
      location: location.trim(),
      meetingType: meetingType,
      date: localDateStr,
      time: selectedTime,
      notes: notes.trim(),
      timezone: timezone,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/brookscontainer/schedule-meeting`, bookingDetails);
      if (response.data && response.data.result) {
        setMeetLink(response.data.meetLink || '');
        toast.success('Meeting scheduled successfully!');
        setStep(3); // Advance to Success page
      } else {
        toast.error(response.data.message || 'Failed to schedule meeting. Please try again.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to schedule meeting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Cancel / Reset Modal
  const handleCancel = () => {
    setStep(1);
    setName('');
    setEmail('');
    setPhone('');
    setSelectedCountry(countries[0]);
    setLocation('');
    setMeetingType('Online');
    setNotes('');
    setMeetLink('');
    setErrors({});
    setTimezone('India');
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      centered
      dialogClassName="custom-booking-modal"
      backdrop="static"
    >
      <Modal.Body className="booking-modal-body">

        {/* Close Button */}
        <button className="booking-modal-close" onClick={handleCancel} aria-label="Close modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Logo Header */}
        <div className="booking-modal-logo-row">
          <div className="booking-modal-logo-wrapper">
            <img src={Logo} alt="HKAU Logo" className="booking-modal-logo" />
          </div>
        </div>

        {/* STEP 1: DATE & TIME SELECTION */}
        {step === 1 && (
          <div className="booking-step-one">

            {/* Header Title Section */}
            <div className="booking-header-details">
              <div className="booking-header-left">
                <h2 className="booking-title">
                  Book a Meeting with<br />
                  <span className="text-orange">Brooks Container</span>
                </h2>
                <p className="booking-subtitle">
                  Choose a convenient time to discuss your container investment plan.
                </p>
              </div>

              <div className="booking-header-right">
                {/* Meeting Duration */}
                <div className="booking-badge-item">
                  <div className="badge-icon-circle">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </div>
                  <div className="badge-text-box">
                    <span className="badge-title">Meeting Duration</span>
                    <span className="badge-sub">30 Minutes</span>
                  </div>
                </div>

                {/* Need Help */}
                <div className="booking-badge-item">
                  <div className="badge-icon-circle">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                    </svg>
                  </div>
                  <div className="badge-text-box">
                    <span className="badge-title">Need Help?</span>
                    <span className="badge-sub">
                      Our team is here to help you,<br />
                      <a href="tel:+911234567890" className="badge-phone-link">+971 55 996 1317</a>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Split layout: Calendar vs Time Slots */}
            <div className="booking-grid-split">

              {/* Calendar Column */}
              <div className="booking-calendar-col">
                <div className="section-step-indicator">
                  <span className="step-num">1</span>
                  <span className="step-title">SELECT DATE</span>
                </div>

                {/* Calendar Navigation */}
                {/* <div className="calendar-nav-bar" >
                  <button className="calendar-nav-btn" onClick={handlePrevMonth} aria-label="Previous month" style={{ color: "red", backgroundColor: "red" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>
                  <h4 className="calendar-current-month">
                    {monthsList[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h4>
                  <button className="calendar-nav-btn" onClick={handleNextMonth} aria-label="Next month">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div> */}

                <div
                  className="calendar-nav-bar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <button
                    className="calendar-nav-btn"
                    onClick={handlePrevMonth}
                    aria-label="Previous month"
                    disabled={currentDate.getFullYear() <= new Date().getFullYear() && currentDate.getMonth() <= new Date().getMonth()}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: "4px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1E3357", // Dark blue like the image
                      outline: "none",
                      boxShadow: "none",
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>

                  <h4
                    className="calendar-current-month"
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#1E3357",
                    }}
                  >
                    {monthsList[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h4>

                  <button
                    className="calendar-nav-btn"
                    onClick={handleNextMonth}
                    aria-label="Next month"
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: "4px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1E3357",
                      outline: "none",
                      boxShadow: "none",
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="calendar-grid-wrapper">
                  {/* Days of Week header */}
                  <div className="calendar-days-header">
                    {daysOfWeek.map(day => (
                      <span key={day} className="day-header-cell">{day}</span>
                    ))}
                  </div>

                  {/* Days grid */}
                  <div className="calendar-days-grid">
                    {getCalendarDays().map((item, idx) => {
                      const isSelected = isSameDay(item.date, selectedDate);
                      const isToday = isSameDay(item.date, new Date());
                      const isPast = isPastDay(item.date);
                      const isBordered = item.isCurrentMonth && item.day === 12 && !isPast;
                      return (
                        <button
                          key={idx}
                          type="button"
                          className={`day-cell ${!item.isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'day-selected' : ''} ${isToday ? 'day-today' : ''} ${isBordered ? 'day-bordered' : ''} ${isPast ? 'day-disabled' : ''}`}
                          onClick={() => !isPast && handleDateSelect(item.date)}
                          disabled={isPast}
                        >
                          {item.day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Vertical Separator */}
              <div className="booking-vertical-divider"></div>

              {/* Time Slots Column */}
              <div className="booking-time-col">
                <div className="section-step-indicator">
                  <span className="step-num">2</span>
                  <span className="step-title">SELECT TIME</span>
                </div>

                <div className="selected-day-label">
                  {getFormattedSelectedDate()}
                </div>

                <div className="time-slots-list">
                  {timeSlots.map((time) => {
                    const isTimeSelected = time === selectedTime;
                    const isTimePast = isPastTimeSlot(time, selectedDate);
                    return (
                      <button
                        key={time}
                        type="button"
                        className={`time-slot-btn ${isTimeSelected ? 'time-selected' : ''} ${isTimePast ? 'time-disabled' : ''}`}
                        onClick={() => !isTimePast && setSelectedTime(time)}
                        disabled={isTimePast}
                      >
                        <div className="time-btn-content">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="time-clock-icon">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          <span>{time}</span>
                        </div>
                        {isTimeSelected && (
                          <div className="time-check-circle">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="16 9 11 14 8 11"></polyline>
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="timezone-selector-container">
                  <label htmlFor="timezone-select" className="timezone-select-label">Timezone:</label>
                  <select
                    id="timezone-select"
                    value={timezone}
                    onChange={(e) => handleTimezoneChange(e.target.value)}
                    className="timezone-select-input"
                  >
                    <option value="India">India (GMT +5:30)</option>
                    <option value="Dubai">Dubai (GMT +4:00)</option>
                  </select>
                </div>

                <div className="timezone-footer-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                  <span>
                    All times are shown in {timezone === 'Dubai' ? '(GMT +4:00) Gulf Standard Time' : '(GMT +5:30) India Standard Time'}
                  </span>
                </div>
              </div>

            </div>

            {/* Step 1 Actions Footer */}
            <div className="booking-modal-footer">
              <button className="footer-cancel-btn" onClick={handleCancel}>
                CANCEL
              </button>
              <button className="footer-next-btn" onClick={handleNextStep}>
                ADD YOU DETAILS
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px' }}>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            </div>

          </div>
        )}

        {/* STEP 2: ENTER BOOKING DETAILS */}
        {step === 2 && (
          <div className="booking-step-two">
            {/* Header Title Section */}
            <div className="booking-header-details">
              <div className="booking-header-left">
                <h2 className="booking-title">
                  Book a Meeting with<br />
                  <span className="text-orange">Brooks Container</span>
                </h2>
                <p className="booking-subtitle">
                  Choose a convenient time to discuss your container investment plan.
                </p>
              </div>

              <div className="booking-header-right">
                {/* Meeting Duration */}
                <div className="booking-badge-item">
                  <div className="badge-icon-circle">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </div>
                  <div className="badge-text-box">
                    <span className="badge-title">Meeting Duration</span>
                    <span className="badge-sub">30 Minutes</span>
                  </div>
                </div>

                {/* Need Help */}
                <div className="booking-badge-item">
                  <div className="badge-icon-circle">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                    </svg>
                  </div>
                  <div className="badge-text-box">
                    <span className="badge-title">Need Help?</span>
                    <span className="badge-sub">
                      Our team is here to help you,<br />
                      <a href="tel:+911234567890" className="badge-phone-link">+971 55 996 1317</a>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="section-step-indicator" style={{ marginBottom: '24px' }}>
              <span className="step-num">3</span>
              <span className="step-title">YOUR DETAILS</span>
            </div>

            <form onSubmit={handleDetailsSubmit} className="booking-details-form">
              <div className="details-form-grid">

                {/* Full Name */}
                <div className="details-form-group">
                  <label className="details-input-label">Full Name</label>
                  <div className={`details-input-icon-wrapper ${errors.name ? 'input-error' : ''}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="input-field-icon">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      className="details-text-input"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (errors.name) setErrors(prev => ({ ...prev, name: null }));
                      }}
                    />
                  </div>
                  {errors.name && <span className="details-error-msg">{errors.name}</span>}
                </div>

                {/* Email Address */}
                <div className="details-form-group">
                  <label className="details-input-label">Email Address</label>
                  <div className={`details-input-icon-wrapper ${errors.email ? 'input-error' : ''}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="input-field-icon">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      className="details-text-input"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors(prev => ({ ...prev, email: null }));
                      }}
                    />
                  </div>
                  {errors.email && <span className="details-error-msg">{errors.email}</span>}
                </div>

                {/* Phone Number */}
                <div className="details-form-group">
                  <label className="details-input-label">Phone Number</label>
                  <div className={`phone-input-wrapper-custom ${errors.phone ? 'input-error' : ''}`}>
                    <div className="phone-country-selector">
                      <img src={selectedCountry.flag} alt={selectedCountry.name} className="country-flag-icon" />
                      <span className="country-code-text">{selectedCountry.code}</span>
                      <svg className="country-arrow-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>

                      {/* Hidden Accessible Select */}
                      <select
                        value={selectedCountry.id}
                        onChange={(e) => {
                          const found = countries.find(c => c.id === e.target.value);
                          if (found) {
                            setSelectedCountry(found);
                            if (errors.phone) setErrors(prev => ({ ...prev, phone: null }));

                            // Truncate existing phone number if it exceeds the new country's limit
                            const maxDigits = {
                              'IN': 10,
                              'US': 10,
                              'AE': 9,
                              'GB': 10,
                              'AU': 9,
                              'SG': 8
                            }[found.id] || 15;

                            let val = phone.replace(/[^0-9\s+\-()]/g, '');
                            let allowedDigits = maxDigits;
                            const rawDigits = val.replace(/\D/g, '');
                            const cleanCC = found.code.replace('+', '');

                            if (val.trim().startsWith('+')) {
                              allowedDigits += 1;
                            }
                            if (rawDigits.startsWith(cleanCC)) {
                              allowedDigits += cleanCC.length;
                            } else if (rawDigits.startsWith('0')) {
                              allowedDigits += 1;
                            }

                            const rawDigitCount = val.replace(/\D/g, '').length;
                            if (rawDigitCount > allowedDigits) {
                              let currentDigitCount = 0;
                              let truncatedVal = '';
                              for (let char of val) {
                                if (/\d/.test(char)) {
                                  if (currentDigitCount < allowedDigits) {
                                    truncatedVal += char;
                                    currentDigitCount++;
                                  }
                                } else {
                                  truncatedVal += char;
                                }
                              }
                              setPhone(truncatedVal);
                            }
                          }
                        }}
                        className="hidden-select-element"
                      >
                        {countries.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <input
                      type="tel"
                      placeholder="Enter your phone number"
                      className="phone-digits-input"
                      value={phone}
                      onChange={(e) => {
                        let val = e.target.value.replace(/[^0-9\s+\-()]/g, '');

                        const maxDigits = {
                          'IN': 10,
                          'US': 10,
                          'AE': 9,
                          'GB': 10,
                          'AU': 9,
                          'SG': 8
                        }[selectedCountry.id] || 15;

                        let allowedDigits = maxDigits;
                        const rawDigits = val.replace(/\D/g, '');
                        const cleanCC = selectedCountry?.code?.replace('+', '') || '';

                        if (val.trim().startsWith('+')) {
                          allowedDigits += 1;
                        }
                        if (rawDigits.startsWith(cleanCC)) {
                          allowedDigits += cleanCC.length;
                        } else if (rawDigits.startsWith('0')) {
                          allowedDigits += 1;
                        }

                        const rawDigitCount = val.replace(/\D/g, '').length;
                        if (rawDigitCount <= allowedDigits) {
                          setPhone(val);
                        } else {
                          let currentDigitCount = 0;
                          let truncatedVal = '';
                          for (let char of val) {
                            if (/\d/.test(char)) {
                              if (currentDigitCount < allowedDigits) {
                                truncatedVal += char;
                                currentDigitCount++;
                              }
                            } else {
                              truncatedVal += char;
                            }
                          }
                          setPhone(truncatedVal);
                        }

                        if (errors.phone) setErrors(prev => ({ ...prev, phone: null }));
                      }}
                    />
                  </div>
                  {errors.phone && <span className="details-error-msg">{errors.phone}</span>}
                </div>

                {/* Location/City */}
                <div className="details-form-group">
                  <label className="details-input-label">Location/City</label>
                  <div className={`details-input-icon-wrapper ${errors.location ? 'input-error' : ''}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="input-field-icon">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <input
                      type="text"
                      placeholder="Enter your location or city"
                      className="details-text-input"
                      value={location}
                      onChange={(e) => {
                        setLocation(e.target.value);
                        if (errors.location) setErrors(prev => ({ ...prev, location: null }));
                      }}
                    />
                  </div>
                  {errors.location && <span className="details-error-msg">{errors.location}</span>}
                </div>

                {/* Meeting Mode / Type select */}
                <div className="details-form-group">
                  <label className="details-input-label">Preferred Meeting Mode</label>
                  <div className="details-input-icon-wrapper">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="input-field-icon">
                      <polygon points="23 7 16 12 23 17 23 7"></polygon>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                    </svg>
                    <select
                      className="details-text-input"
                      value={meetingType}
                      onChange={(e) => setMeetingType(e.target.value)}
                    >
                      <option value="Online">Online Meeting</option>
                      <option value="Offline">Offline Meeting</option>
                    </select>
                  </div>
                </div>

                {/* Message Notes */}
                <div className="details-form-group full-row">
                  <label className="details-input-label">Message (Optional)</label>
                  <textarea
                    placeholder="Tell us about your container requirement"
                    className="details-textarea-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>

              </div>

              {/* Privacy security banner */}
              <div className="details-privacy-banner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="privacy-shield-icon">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <polyline points="9 11 11 13 15 9"></polyline>
                </svg>
                <span className="privacy-banner-text">
                  Your information is secure and will never be shared with third parties.
                </span>
              </div>

              {/* Step 2 Actions Footer */}
              <div className="booking-modal-footer">
                <button type="button" className="footer-cancel-btn" onClick={handleCancel}>
                  CANCEL
                </button>
                <button type="submit" className="footer-next-btn" disabled={loading}>
                  {loading ? 'BOOKING...' : 'CONFIRM MEETING'}
                  {!loading && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px' }}>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* STEP 3: BOOKING SUCCESS */}
        {step === 3 && (
          <div className="booking-step-three">
            <div className="success-icon-wrapper">
              <div className="success-checkmark-circle">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            </div>

            <h2 className="success-booking-title">
              {meetingType === 'Offline' ? 'Request Received!' : 'Meeting Booked Successfully!'}
            </h2>
            <p className="success-booking-subtitle">
              {meetingType === 'Offline' ? (
                <>Thank you, <strong>{name}</strong>. Your offline meeting request has been received. The Brooks team will contact you soon.</>
              ) : (
                <>Thank you, <strong>{name}</strong>. Your meeting has been scheduled with the Brooks Container investment team.</>
              )}
            </p>

            <div className="success-summary-card">
              <h4 className="summary-card-title">Meeting Schedule Details</h4>
              <div className="summary-card-row">
                <span className="row-label">Date:</span>
                <span className="row-val">{getFormattedSelectedDate()}</span>
              </div>
              <div className="summary-card-row">
                <span className="row-label">Time:</span>
                <span className="row-val">{selectedTime} ({timezone === 'Dubai' ? 'Gulf Standard Time' : 'India Standard Time'})</span>
              </div>
              <div className="summary-card-row">
                <span className="row-label">Duration:</span>
                <span className="row-val">30 Minutes</span>
              </div>
              <div className="summary-card-row">
                <span className="row-label">Meeting Mode:</span>
                <span className="row-val">{meetingType} Meeting</span>
              </div>
              <div className="summary-card-row">
                <span className="row-label">Contact Email:</span>
                <span className="row-val">{email}</span>
              </div>
              {meetLink && (
                <div className="summary-card-row">
                  <span className="row-label">Meet Link:</span>
                  <span className="row-val">
                    <a href={meetLink} target="_blank" rel="noopener noreferrer" className="meet-link-anchor">
                      {meetLink}
                    </a>
                  </span>
                </div>
              )}
            </div>

            <p className="success-info-footer">
              {meetingType === 'Offline'
                ? 'A confirmation email has been sent to your email address.'
                : 'A calendar invitation with meeting links and details has been sent to your email address.'}
            </p>

            {meetLink && (
              <div className="text-center" style={{ margin: '20px 0' }}>
                <a
                  href={meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-next-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    borderRadius: '8px'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                    <polygon points="23 7 16 12 23 17 23 7"></polygon>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                  </svg>
                  JOIN MEETING
                </a>
              </div>
            )}

            <div className="booking-modal-footer justify-content-center">
              <button className="success-close-btn" onClick={handleCancel}>
                CLOSE WINDOW
              </button>
            </div>
          </div>
        )}

      </Modal.Body>
    </Modal>
  );
};

export default BookingModal;
